import { getPortfolioImages } from "@/clients/assets";
import { useBusiness } from "@/contracts/useBusiness";
import { useLanguage } from "@/hooks/useLanguage";
import { type Language } from "@/lib/translations";
import { Clock, Instagram, MapPin, Phone, Scissors } from "lucide-react";
import { Link } from "react-router-dom";

function LanguageToggle(props: {
  language: Language;
  setLanguage: (lang: Language) => void;
}) {
  const { language, setLanguage } = props;

  const base =
    "px-2 py-1 rounded transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--gold)/0.35)]";

  const selected = "text-[hsl(var(--gold))] font-semibold";
  const unselected =
    "text-[hsl(var(--foreground))] hover:text-[hsl(var(--accent))]";

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => setLanguage("en")}
        className={`${base} ${language === "en" ? selected : unselected}`}
        aria-pressed={language === "en"}
      >
        EN
      </button>

      <span className="text-muted-foreground">|</span>

      <button
        type="button"
        onClick={() => setLanguage("pt")}
        className={`${base} ${language === "pt" ? selected : unselected}`}
        aria-pressed={language === "pt"}
      >
        PT
      </button>

      <span className="text-muted-foreground">|</span>

      <button
        type="button"
        onClick={() => setLanguage("es")}
        className={`${base} ${language === "es" ? selected : unselected}`}
        aria-pressed={language === "es"}
      >
        ES
      </button>
    </div>
  );
}

function SectionHeaderPremium(props: {
  label: string;
  title: string;
  subtitle?: string;
}) {
  const { label, title, subtitle } = props;

  return (
    <div className="mb-14 md:mb-16">
      <div className="flex items-center gap-4">
        <span className="uppercase tracking-[0.35em] text-xs font-semibold text-[hsl(var(--gold))]">
          {label}
        </span>
        <span className="h-px w-20 bg-[hsl(var(--gold)/0.25)]" />
      </div>

      <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground">
        {title}
      </h2>

      {subtitle ? (
        <p className="mt-5 max-w-2xl text-[hsl(var(--accent))] leading-relaxed">
          {subtitle}
        </p>
      ) : null}

      <div className="mt-10 h-px w-24 bg-[hsl(var(--gold)/0.22)]" />
    </div>
  );
}

function ServiceCardPremium(props: {
  badge: string;
  name: string;
  description: string;
  duration: string;
  price: string;
  cta: string;
  bookingHref: string;
}) {
  const { badge, name, description, duration, price, cta, bookingHref } = props;

  return (
    <div className="fx-service-card p-7 md:p-8">
      <span className="fx-service-corner tl" aria-hidden="true" />
      <span className="fx-service-corner br" aria-hidden="true" />

      <div className="flex items-center gap-3">
        <span className="h-px w-8 bg-[hsl(var(--gold)/0.28)]" />
        <span className="uppercase tracking-[0.32em] text-[11px] font-semibold text-[hsl(var(--gold)/0.7)]">
          {badge}
        </span>
      </div>

      <h3 className="mt-6 font-serif text-2xl font-semibold text-foreground">
        {name}
      </h3>

      <p className="mt-3 text-sm leading-relaxed text-[hsl(var(--accent)/0.65)]">
        {description}
      </p>

      <div className="mt-5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-[hsl(var(--accent)/0.7)]">
        <Clock className="h-4 w-4 text-[hsl(var(--gold)/0.75)]" />
        <span>{duration}</span>
      </div>

      <div className="mt-9 flex items-center justify-between gap-6">
        <div className="fx-price-premium font-serif text-3xl font-semibold text-[hsl(var(--gold))]">
          {price}
        </div>

        <a
          href={bookingHref}
          target="_blank"
          rel="noopener noreferrer"
          className={[
            "fx-btn-primary",
            "px-6 py-3 rounded-md text-sm font-semibold uppercase tracking-[0.22em]",
            "shadow-lg hover:shadow-xl transition-shadow",
            "border border-[hsl(var(--gold)/0.22)] hover:border-[hsl(var(--gold)/0.38)]",
          ].join(" ")}
        >
          {cta}
        </a>
      </div>
    </div>
  );
}

type DayHours =
  | { closed: true }
  | {
      open: string;
      close: string;
      closed?: false;
    };

const Index = () => {
  const { language, setLanguage, t } = useLanguage();

  const contract = useBusiness();
  const business = contract.business;

  const portfolioImages = getPortfolioImages(6);

  const brandName = business.displayName || business.legalName || "";

  const location = business.location;
  const addressText = location
    ? [location.addressLine1, location.cityRegionPostal]
        .filter(Boolean)
        .join(", ")
    : "36 Federal Rd, Danbury, CT 06810";

  const mapsUrl =
    business.links?.maps ||
    `https://maps.google.com/?q=${encodeURIComponent(addressText)}`;

  const phoneDisplay = business.contact?.phone || "(203) 417-3905";
  const phoneDigits = phoneDisplay.replace(/\D/g, "");
  const phoneHref = phoneDigits ? `tel:+${phoneDigits}` : "tel:+12034173905";

  const igRaw = business.contact?.instagram || "marriel_barber";
  const igUrl = igRaw.includes("instagram.com")
    ? igRaw
    : `https://instagram.com/${igRaw.replace(/^@/, "")}`;
  const igHandle = igRaw.includes("instagram.com")
    ? `@${igRaw.split("instagram.com/")[1]?.replace(/\/.*/, "") ?? "instagram"}`
    : igRaw.startsWith("@")
      ? igRaw
      : `@${igRaw}`;

  const waRaw =
    business.contact?.whatsapp || business.contact?.phone || "12034173905";
  const waDigits = waRaw.replace(/\D/g, "");

  const waMessageByLanguage: Record<Language, string> = {
    en: `Hello, I'd like to book an appointment at ${brandName}.`,
    pt: `Olá, gostaria de agendar um horário na ${brandName}.`,
    es: `Hola, me gustaría reservar una cita en ${brandName}.`,
  };

  const waHref = waDigits
    ? `https://wa.me/${waDigits}?text=${encodeURIComponent(
        waMessageByLanguage[language],
      )}`
    : `https://wa.me/12034173905?text=${encodeURIComponent(
        waMessageByLanguage[language],
      )}`;

  const bookingUrl = business.links?.booking || waHref;

  const mapEmbedUrl =
    business.map?.googleMapsEmbedUrl ||
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2997.8088848097166!2d-73.45598548457441!3d41.39452997926054!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e7f92a1234567%3A0x1234567890abcdef!2s36%20Federal%20Rd%2C%20Danbury%2C%20CT%2006810!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus";

  const services =
    business.services?.map((service) => ({
      id: service.id,
      name: service.name[language],
      description: service.description[language],
      duration: service.duration,
      price: service.price,
    })) ?? [];

  const hours = business.hours?.days;

  function formatTime(time?: string) {
    if (!time) return "";
    const [rawHour, rawMinute] = time.split(":");
    const hour = Number(rawHour);
    const minute = rawMinute ?? "00";
    const suffix = hour >= 12 ? "PM" : "AM";
    const normalizedHour = hour % 12 === 0 ? 12 : hour % 12;

    return `${normalizedHour}:${minute} ${suffix}`;
  }

  function formatDayHours(day?: DayHours) {
    if (!day) return "";
    if ("closed" in day && day.closed) {
      return t.visit.closed;
    }
    return `${formatTime(day.open)} – ${formatTime(day.close)}`;
  }

  function buildHeroHoursSummary() {
    const tuesday = formatDayHours(hours?.tuesday);
    const wednesday = formatDayHours(hours?.wednesday);
    const thursday = formatDayHours(hours?.thursday);
    const friday = formatDayHours(hours?.friday);
    const saturday = formatDayHours(hours?.saturday);
    const sunday = formatDayHours(hours?.sunday);
    const monday = formatDayHours(hours?.monday);

    const sameTueWed =
      tuesday &&
      wednesday &&
      tuesday === wednesday &&
      hours?.tuesday &&
      hours?.wednesday &&
      !("closed" in hours.tuesday && hours.tuesday.closed) &&
      !("closed" in hours.wednesday && hours.wednesday.closed);

    const sameThuFri =
      thursday &&
      friday &&
      thursday === friday &&
      hours?.thursday &&
      hours?.friday &&
      !("closed" in hours.thursday && hours.thursday.closed) &&
      !("closed" in hours.friday && hours.friday.closed);

    const parts: string[] = [];

    if (sameTueWed) {
      parts.push(
        `${t.visit.days.tuesday}–${t.visit.days.wednesday}: ${tuesday}`,
      );
    } else {
      if (tuesday) parts.push(`${t.visit.days.tuesday}: ${tuesday}`);
      if (wednesday) parts.push(`${t.visit.days.wednesday}: ${wednesday}`);
    }

    if (sameThuFri) {
      parts.push(
        `${t.visit.days.thursday}–${t.visit.days.friday}: ${thursday}`,
      );
    } else {
      if (thursday) parts.push(`${t.visit.days.thursday}: ${thursday}`);
      if (friday) parts.push(`${t.visit.days.friday}: ${friday}`);
    }

    if (saturday) {
      parts.push(`${t.visit.days.saturday}: ${saturday}`);
    }

    const closedDays: string[] = [];
    if (monday === t.visit.closed) closedDays.push(t.visit.days.monday);
    if (sunday === t.visit.closed) closedDays.push(t.visit.days.sunday);

    if (closedDays.length > 0) {
      parts.push(`${closedDays.join("/")} : ${t.visit.closed}`);
    }

    return parts.join(" · ");
  }

  const heroHours = buildHeroHoursSummary();

  const scheduleRows: Array<{ label: string; value: string }> = [
    { label: t.visit.days.monday, value: formatDayHours(hours?.monday) },
    { label: t.visit.days.tuesday, value: formatDayHours(hours?.tuesday) },
    { label: t.visit.days.wednesday, value: formatDayHours(hours?.wednesday) },
    { label: t.visit.days.thursday, value: formatDayHours(hours?.thursday) },
    { label: t.visit.days.friday, value: formatDayHours(hours?.friday) },
    { label: t.visit.days.saturday, value: formatDayHours(hours?.saturday) },
    { label: t.visit.days.sunday, value: formatDayHours(hours?.sunday) },
  ].filter((row) => row.value);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-serif text-xl font-semibold text-foreground inline-flex items-center">
              <Link
                to="/"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="flex items-center gap-2 transition-all duration-300 hover:opacity-90 hover:scale-[1.02]"
              >
                <img
                  src="/clients/marriel/assets/marriel-logo.png"
                  alt="Marriel Barber"
                  className="h-10 w-auto"
                />
              </Link>
            </span>
          </div>

          <nav className="hidden md:flex items-center gap-8">
            <a
              href="#services"
              className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
            >
              {t.nav.services}
            </a>
            <a
              href="#portfolio"
              className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
            >
              {t.nav.portfolio}
            </a>
            <a
              href="#contact"
              className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
            >
              {t.nav.contact}
            </a>
          </nav>

          <div className="flex items-center gap-4">
            <LanguageToggle language={language} setLanguage={setLanguage} />

            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="fx-btn-primary px-5 py-2.5 rounded-md text-sm font-semibold shadow-lg hover:shadow-xl transition-shadow border border-[hsl(var(--gold)/0.18)] hover:border-[hsl(var(--gold)/0.32)]"
            >
              {t.nav.bookNow}
            </a>
          </div>
        </div>
      </header>

      <section className="relative min-h-screen flex items-center justify-start pt-24 md:pt-28">
        <picture className="absolute inset-0">
          <source
            media="(max-width: 768px)"
            srcSet="/clients/marriel/assets/hero-mobile-background-black.jpg"
          />

          <img
            src="/clients/marriel/assets/hero-background-black.jpg"
            alt="Marriel Barber"
            className="h-full w-full object-cover"
            style={{ objectPosition: "center -5%" }}
            loading="eager"
            decoding="async"
          />
        </picture>

        <div className="absolute inset-0 bg-gradient-to-r from-background/70 via-background/35 to-transparent" />

        <div className="relative z-10 w-full px-6">
          <div className="mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-12 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center justify-start gap-4 mb-8">
                <div className="w-16 h-px bg-[hsl(var(--gold)/0.40)]" />
                <Scissors className="w-5 h-5 rotate-90 text-[hsl(var(--gold))]" />
                <div className="w-16 h-px bg-[hsl(var(--gold)/0.40)]" />
              </div>

              <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-bold mb-6 tracking-tight text-white">
                {t.hero.title}{" "}
                <span className="text-[hsl(var(--accent))]">
                  {t.hero.titleAccent}
                </span>{" "}
                {t.hero.titleEnd}
              </h1>

              <p className="text-lg md:text-xl text-white/75 mb-10 max-w-2xl leading-relaxed">
                {t.hero.subtitle}
              </p>

              <div className="flex flex-col sm:flex-row items-start gap-4 mb-10">
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fx-btn-primary px-8 py-4 rounded-md text-base font-semibold shadow-lg hover:shadow-xl transition-all border border-[hsl(var(--gold)/0.18)] hover:border-[hsl(var(--gold)/0.32)]"
                >
                  {t.hero.bookAppointment}
                </a>

                <a
                  href="#services"
                  className="px-8 py-4 rounded-md text-base font-semibold transition-all border border-[hsl(var(--gold)/0.22)] text-[hsl(var(--accent))] hover:bg-white/10"
                >
                  {t.hero.viewServices}
                </a>
              </div>

              <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start gap-6 text-sm text-white/60">
                <div className="flex items-center gap-2 text-white">
                  <Clock className="w-4 h-4 text-[hsl(var(--gold))]" />
                  <span>{heroHours || t.hero.hours}</span>
                </div>

                <div className="flex items-center gap-2 text-white">
                  <MapPin className="w-4 h-4 text-[hsl(var(--gold))]" />
                  <span>{addressText}</span>
                </div>
              </div>
            </div>

            <div className="hidden lg:block lg:col-span-5" />
          </div>
        </div>
      </section>

      <section id="services" className="py-24 md:py-28 px-6">
        <div className="max-w-7xl mx-auto">
          <SectionHeaderPremium
            label={t.services.kicker ?? "PREMIUM"}
            title={`${t.services.title} ${t.services.titleAccent}`}
            subtitle={t.services.subtitle}
          />

          <div className="grid gap-6 md:gap-7 md:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => (
              <ServiceCardPremium
                key={service.id}
                badge={t.services.badge ?? "PREMIUM"}
                name={service.name}
                description={service.description}
                duration={service.duration}
                price={service.price}
                cta={t.services.bookNow}
                bookingHref={bookingUrl}
              />
            ))}
          </div>
        </div>
      </section>

      <section
        id="portfolio"
        className="py-24 md:py-28 px-6 bg-[radial-gradient(120%_90%_at_20%_0%,hsl(var(--accent)/0.08)_0%,transparent_55%)]"
      >
        <div className="max-w-7xl mx-auto">
          <SectionHeaderPremium
            label={t.portfolio.kicker ?? "PORTFOLIO"}
            title={`${t.portfolio.title} ${t.portfolio.titleAccent}`}
            subtitle={t.portfolio.subtitle}
          />

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-7">
            {portfolioImages.map((image, index) => (
              <div key={index} className="fx-service-card p-0">
                <span className="fx-service-corner tl" aria-hidden="true" />
                <span className="fx-service-corner br" aria-hidden="true" />

                <div className="relative aspect-[3/4] overflow-hidden rounded-[calc(var(--radius)+0.25rem)]">
                  <img
                    src={image}
                    alt={`Portfolio ${index + 1}`}
                    className="h-full w-full object-cover transition-transform duration-700 will-change-transform hover:scale-[1.04]"
                  />

                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent opacity-60" />
                  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_80%_0%,hsl(var(--gold)/0.10)_0%,transparent_60%)] opacity-60" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="contact"
        className="py-24 md:py-28 px-6 bg-[radial-gradient(120%_90%_at_20%_0%,hsl(var(--gold)/0.06)_0%,transparent_55%)]"
      >
        <div className="max-w-7xl mx-auto">
          <SectionHeaderPremium
            label={t.visit.kicker ?? "VISIT"}
            title={`${t.visit.title}${t.visit.titleAccent ? ` ${t.visit.titleAccent}` : ""}`}
            subtitle={t.visit.subtitle}
          />

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="fx-service-card p-7 lg:p-8">
              <span className="fx-service-corner tl" aria-hidden="true" />
              <span className="fx-service-corner br" aria-hidden="true" />

              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[hsl(var(--muted)/0.55)] flex items-center justify-center ring-1 ring-[hsl(var(--gold)/0.18)]">
                  <Scissors className="w-6 h-6 text-[hsl(var(--gold))]" />
                </div>
                <div>
                  <h3 className="font-serif text-xl font-semibold text-foreground">
                    {brandName}
                  </h3>
                  <p className="text-muted-foreground text-sm">Barbershop</p>
                </div>
              </div>

              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 mb-6 group"
              >
                <MapPin className="w-5 h-5 text-[hsl(var(--gold))] mt-0.5 flex-shrink-0" />
                <span className="text-foreground group-hover:text-[hsl(var(--accent))] transition-colors">
                  {addressText}
                </span>
              </a>

              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-5 h-5 text-[hsl(var(--gold))] flex-shrink-0" />
                  <span className="text-foreground font-medium">
                    {t.visit.openingHours}
                  </span>
                </div>

                <div className="ml-8 space-y-1.5 text-sm">
                  {scheduleRows.map((row) => (
                    <div key={row.label} className="flex justify-between gap-6">
                      <span className="text-muted-foreground">{row.label}</span>
                      <span className="text-foreground text-right">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <a
                href={phoneHref}
                className="flex items-center gap-3 mb-6 group"
              >
                <Phone className="w-5 h-5 text-[hsl(var(--gold))] flex-shrink-0" />
                <span className="text-foreground group-hover:text-[hsl(var(--accent))] transition-colors">
                  {phoneDisplay}
                </span>
              </a>

              <a
                href={igUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 mb-8 group"
              >
                <Instagram className="w-5 h-5 text-[hsl(var(--gold))] flex-shrink-0" />
                <span className="text-foreground group-hover:text-[hsl(var(--accent))] transition-colors">
                  {igHandle}
                </span>
              </a>

              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full fx-btn-primary px-6 py-3.5 rounded-md text-base font-semibold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 border border-[hsl(var(--gold)/0.18)] hover:border-[hsl(var(--gold)/0.32)]"
              >
                <MapPin className="w-5 h-5" />
                {t.visit.getDirections}
              </a>
            </div>

            <div className="fx-service-card p-0 overflow-hidden min-h-[400px] lg:min-h-[500px]">
              <span className="fx-service-corner tl" aria-hidden="true" />
              <span className="fx-service-corner br" aria-hidden="true" />

              <iframe
                src={mapEmbedUrl}
                width="100%"
                height="100%"
                style={{
                  border: 0,
                  filter: "saturate(0.9) contrast(0.95) brightness(0.95)",
                }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`${brandName} Location`}
                className="w-full h-full min-h-[400px] lg:min-h-[500px]"
              />
            </div>
          </div>
        </div>
      </section>

      <footer className="py-8 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-4">
          <div className="w-full flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Scissors className="w-5 h-5 text-[hsl(var(--gold))]" />
              <span className="font-serif text-lg font-semibold text-foreground">
                {brandName}
              </span>
            </div>
            <p className="text-muted-foreground text-sm">{t.footer.rights}</p>
          </div>

          <p className="text-muted-foreground/60 text-xs">
            Built by{" "}
            <a
              href="https://framixor.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold hover:text-muted-foreground transition-colors"
            >
              Framixor
            </a>
            <span className="opacity-70"> — a technology brand of </span>
            <a
              href="https://loiolapropertygroup.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-muted-foreground transition-colors"
            >
              Loiola Property Group
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
